/*
    Transaction activity is handled on the backend (statements) + frontend, so we are overloading to make different types work in both instances
*/
import {
  Advance as GqlAdvance,
  Disbursement as GqlDisbursement,
  Draw as GqlDraw,
  FlexModification as GqlFlexModification,
  LiquidationRecordEntry as GqlLiquidationRecordEntry,
  Payment as GqlPayment,
  PrincipalModification as GqlPrincipalModification,
  Writeoff as GqlWriteoff,
} from '@willow/graphql-iso/src/app';
import { TransactionActivity as TransactionActivityPortal } from '@willow/graphql-iso/src/portal';
import {
  Advance,
  Disbursement,
  Fee,
  FlexModification,
  Payment,
  PrincipalModification,
  Writeoff,
} from '@willow/types-iso';

export type TransactionActivityItem =
  | Payment
  | Disbursement
  | Fee
  | Advance
  | FlexModification
  | PrincipalModification
  | Writeoff;
export type GqlTransactionActivityItem =
  | GqlPayment
  | GqlDisbursement
  | GqlDraw
  | GqlAdvance
  | GqlFlexModification
  | GqlPrincipalModification
  | GqlWriteoff
  | GqlLiquidationRecordEntry;

const getTransactionDate = (transaction: TransactionActivityItem | GqlTransactionActivityItem): Date => {
  let date;

  if ('paymentDate' in transaction) {
    // Payment type
    date = transaction.paymentDate;
  } else if ('chargeDate' in transaction) {
    // Fee type
    date = transaction.chargeDate;
  } else if ('date' in transaction) {
    // Disbursement type
    date = transaction.date;
  } else if ('effectiveDate' in transaction) {
    // PrincipalModification type
    // Writeoff type
    date = transaction.effectiveDate;
  } else if ('dateReceived' in transaction) {
    // LiquidationRecordEntry type. Only the synthesized reversal row keeps `reversedAt`
    // (see getLiquidationActivityItems), so it sorts at the reversal time while the
    // original row sorts at `dateReceived`.
    date = transaction.reversedAt ?? transaction.dateReceived;
  }

  return new Date(date);
};

export function sortTransactions(transactions: TransactionActivityItem[]): TransactionActivityItem[];
export function sortTransactions(transactions: GqlTransactionActivityItem[]): GqlTransactionActivityItem[];
export function sortTransactions(
  transactions: TransactionActivityItem[] | GqlTransactionActivityItem[],
): TransactionActivityItem[] | GqlTransactionActivityItem[] {
  // Post-MVP this doesn't necessarily work as payment dates may have the same date and we dont have datetime stamp
  if (!transactions?.length) return [];

  const sortedByDate = transactions.sort((a, b) => {
    const dateA = getTransactionDate(a).getTime();
    const dateB = getTransactionDate(b).getTime();

    // Return most recent date first
    return dateB > dateA ? 1 : -1;
  });

  return sortedByDate;
}

interface ObjectWithDate {
  date: string;
}
export type TransactionActivityStatement = {
  date: string;
  description: string;
  amount: string;
  isCharge: boolean;
  isDeferred: boolean;
};
export function sortTransactionsActivity(transactions: TransactionActivityPortal[]): TransactionActivityPortal[];
export function sortTransactionsActivity(transactions: TransactionActivityStatement[]): TransactionActivityStatement[];
export function sortTransactionsActivity<T extends ObjectWithDate>(transactions: T[]): T[];
export function sortTransactionsActivity(
  transactions: TransactionActivityStatement[] | TransactionActivityPortal[],
): TransactionActivityStatement[] | TransactionActivityPortal[] {
  // Post-MVP this doesn't necessarily work as payment dates may have the same date and we dont have datetime stamp
  if (!transactions?.length) return [];

  const sortedByDate = transactions.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();

    // Return most recent date first
    return dateB > dateA ? 1 : -1;
  });

  return sortedByDate;
}
